import { Dialog } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { isEmpty, pickBy } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { SimpleMultiSelect } from '~/src/dash/components/dropdowns/SimpleMultiSelect';
import { GroupSettingsDialog } from '~/src/dash/components/group-settings-dialog';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractVisibleColumns,
  IListViewTableColumnInfo,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { IconToggle } from '~/src/dash/components/toggles/IconToggle';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
  TextInput,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  navigate,
  navigateToBulkUpdateRoute,
  RouteChannelExisting,
  RouteChannelGroupBulkUpdate,
} from '~/src/dash/router/routes';
import { IMenuList } from '~/src/dash/types/menuList';
import { muiColors, publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isDemoAccount } from '~/src/dash/utils/ui';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  IChannelGroupLabel,
  IChannelGroupRow,
  IChannelGroupsUiParams,
  IChannelRow,
} from '~/src/dash/views/channels/api';
import {
  formatDataToLVTRows,
  IChannelGroupsConfig,
} from '~/src/dash/views/channels/ChannelGroups/config';
import { useChannelsStore } from '~/src/dash/views/channels/contexts';

const BULK_ACTION_COLOR = publicaColors.blue;
interface IChannelGroupsProps {
  channelGroups: IChannelGroupRow[];
  channels: Partial<IChannelRow>[];
  labels: IChannelGroupLabel[];
  createChannelGroups: (channelGroups: Array<Partial<IChannelGroupRow>>) => void;
  deleteChannelGroups: (channelIDs: string[]) => Promise<any>;
  fetchChannelGroups: (params: { [key: string]: string | number | boolean | string[] }) => void;
  updateChannelGroups: (channelIDs: string[], updates: Partial<IChannelGroupRow>) => void;
  cfg: IChannelGroupsConfig;
  updateCfg: (params: IChannelGroupsUiParams) => void;
  totalRows: number;
  isActionPending: boolean;
  loadState: LoadingState;
}

export const ChannelGroups = ({
  channelGroups,
  channels,
  labels,
  createChannelGroups,
  deleteChannelGroups,
  fetchChannelGroups,
  updateChannelGroups,
  cfg,
  updateCfg,
  isActionPending,
  totalRows,
  loadState,
}: IChannelGroupsProps) => {
  const { session, user } = useBaseStore();
  const { readAuthorization, writeAuthorization } = useChannelsStore();
  const { cypressClassName, defaultState } = cfg;
  const isSuperUser = hasRoleSuperUser(user.user);

  const [order, setOrder] = useState(defaultState.orderDirection);
  const [orderBy, setOrderBy] = useState(defaultState.orderBy);
  const [searchInput, setSearchInput] = useState('');
  const [currPage, setCurrPage] = useState(defaultState.page);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(defaultState.rowsPerPage);
  const [isTableExpanded, setIsTableExpanded] = useState(defaultState.isTableExpanded);
  const [activeColumns, setActiveColumns] = useState<IMenuList>(
    extractVisibleColumns(cfg.table.columns, defaultState.activeColumnKeys || [], isSuperUser),
  );
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [selectedChannelGroups, setSelectedChannelGroups] = useState<string[]>([]);
  const [activeChannelGroup, setActiveChannelGroup] = useState<IChannelGroupRow | null>(null);
  const [activeChannels, setActiveChannels] = useState<Array<Partial<IChannelGroupRow>> | null>(
    null,
  );

  // search box
  const handleUpdateSearchInput = (text: string) => {
    track('Bidder List - Search', { isSuperUser }, user.user);
    setSearchInput(text);
  };

  // clone a channel group
  const handleCloneChannelGroup = (id: string) => {
    track('Channel Group - Clone Group', { isSuperUser }, user.user);
    const selected = channelGroups.find((group) => String(group.ID) === id);
    if (selected) {
      const activeGroup: IChannelGroupRow = {
        ...selected,
        Name: `${selected.Name} Copy`,
        ID: '0',
        DefaultWeight: 1,
      };
      setActiveChannelGroup(activeGroup);
    }
  };

  // this sets up the changes to force a re-sort
  const handleClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection, label } = column;
    track('Channel List - Column Header Sort', { isSuperUser, sortBy: label }, user.user);
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setSelectedChannelGroups([]);
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setCurrPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setSelectedChannelGroups([]);
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setCurrPage(0);
      });
    }
  };

  // active columns
  const handleChangeActiveColumns = (keys: string[]) => {
    track('Channel List - Select Active Columns', { isSuperUser, activeColumns: keys }, user.user);
    const updatedActiveColumns = {} as IMenuList;
    for (const key in activeColumns) {
      if (activeColumns.hasOwnProperty(key)) {
        updatedActiveColumns[key] = { ...activeColumns[key], selected: keys.includes(key) };
      }
    }
    updateCfg({ activeColumnKeys: keys });
    setActiveColumns(updatedActiveColumns);
  };

  // update page number
  const handleChangePage = (_event: React.MouseEvent | null, page: number) => {
    setCurrPage(page);
  };

  // update rows per page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    track(
      'Channlet List - Change Rows Per Page',
      { isSuperUser, rowsPerPage: updatedRowsPerPage },
      user.user,
    );
    unstable_batchedUpdates(() => {
      setSelectedChannelGroups([]);
      setRowsPerPage(updatedRowsPerPage);
      setCurrPage(0);
    });
  };

  // update of selected channel group(s)
  const handleUpdateChannelGroups = (name: string, defaultWeight: number) => {
    if (!isEmpty(activeChannelGroup)) {
      updateChannelGroups([activeChannelGroup.ID], {
        [activeChannelGroup.ID]: {
          ID: activeChannelGroup.ID,
          Name: name,
          PublisherID: activeChannelGroup.PublisherID,
          ChannelIDs: Array.isArray(activeChannelGroup.ChannelIDs)
            ? activeChannelGroup.ChannelIDs.join(',')
            : activeChannelGroup.ChannelIDs,
          ChannelGroupLabels: activeChannelGroup.ChannelGroupLabels,
          DefaultWeight: defaultWeight,
        },
      });
    }
  };

  // create a new channel group
  const handleCreateChannelGroups = (name: string, defaultWeight: number) => {
    if (!isEmpty(activeChannelGroup)) {
      createChannelGroups([
        {
          Name: name,
          PublisherID: activeChannelGroup.PublisherID,
          ChannelIDs: Array.isArray(activeChannelGroup.ChannelIDs)
            ? activeChannelGroup.ChannelIDs.join(',')
            : activeChannelGroup.ChannelIDs,
          ChannelGroupLabels: activeChannelGroup.ChannelGroupLabels,
          DefaultWeight: defaultWeight,
        },
      ]);
    }
  };

  // update the list of selected channel groups
  const handleChangeSelectedChannels = (id: string) => {
    const selectedIndex = selectedChannelGroups.indexOf(id);
    let selected: string[] = [];
    if (selectedIndex === -1) {
      selected = selected.concat(selectedChannelGroups, id);
    } else if (selectedIndex === 0) {
      selected = selected.concat(selectedChannelGroups.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      selected = selected.concat(selectedChannelGroups.slice(0, -1));
    } else if (selectedIndex > 0) {
      selected = selected.concat(
        selectedChannelGroups.slice(0, selectedIndex),
        selectedChannelGroups.slice(selectedIndex + 1),
      );
    }
    setSelectedChannelGroups(selected);
  };

  // update to select all channel groups
  const handleSelectAllChannels = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedChannelGroups(channelGroups.map((channel) => String(channel.ID)));
    } else {
      setSelectedChannelGroups([]);
    }
  };

  useEffect(() => {
    fetchChannelGroups({
      orderBy,
      order,
      searchInput,
      rowsPerPage,
      currPage,
    });
  }, [searchInput, currPage, rowsPerPage, order, orderBy]);

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      {/* NEW CHANNEL */}
      <Panel style={{ textAlign: 'right' }}>
        <ButtonV2
          onClick={() =>
            setActiveChannelGroup({
              ID: '0',
              Name: '',
              ChannelIDs: [],
              ChannelGroupLabels: [],
              PublisherID: parseInt(session.publisher.ID, 10),
              DefaultWeight: 1,
            })
          }
          className={`cypress-${cypressClassName}-button-createsegment`}
          disabled={!writeAuthorization}
        >
          Create Channel Group
        </ButtonV2>
      </Panel>

      {/* SEARCH & ACTIVE COLUMNS */}
      <Panel highlighted largeHorizontalPadding verticalPadding>
        <Rows>
          <Row style={{ padding: '8px 0px' }}>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <TextInput
                  enableClear
                  handleOnChange={handleUpdateSearchInput}
                  msDelay={800}
                  placeholder='Search by Name, ID'
                  prefix='search'
                  initialValue={searchInput}
                />
              </Col>
              <Col>
                <SearchResultsText
                  numRowsDisplayed={channelGroups.length}
                  numTotalRows={totalRows}
                />
              </Col>
              <Col style={{ marginLeft: 'auto' }}>
                <SimpleMultiSelect
                  buttonStyle={{ padding: '2px 4px 2px 12px' }}
                  resetKeys={cfg.columnSelector.resetKeys}
                  preselectedKeys={defaultState.activeColumnKeys}
                  label='Active Columns'
                  list={activeColumns}
                  handleOnChangeOption={handleChangeActiveColumns}
                  handleOnAccept={handleChangeActiveColumns}
                  singularLabel='Active Column'
                  iconPath='/assets/shared/svg/ColumnEdit.svg'
                />
              </Col>
              <Col>
                <IconToggle
                  activeBackgroundColor='inherit'
                  backgroundColor='inherit'
                  hoverColor={publicaColors.veryLightGray}
                  imagePath='assets/shared/svg/PaddingExpand16.svg'
                  imageStyle={{ height: '28px', padding: '6px' }}
                  isActive={isTableExpanded}
                  setIsActive={(active) => {
                    setIsTableExpanded(active);
                    updateCfg({ isTableExpanded: active });
                  }}
                  tooltipPlacement='top-end'
                  tooltipText={isTableExpanded ? 'Compact Table' : 'Comfortable Table'}
                />
              </Col>
            </Cols>
          </Row>

          {/* BULK UPDATE & DELETE */}
          {selectedChannelGroups.length > 1 && (
            <Row style={{ padding: '8px 0 0 0' }}>
              <Cols
                wrap
                style={{
                  alignItems: 'center',
                  rowGap: spacing.extraSmall,
                }}
              >
                <Col>
                  <Text small bold color={muiColors.checkbox}>
                    {`${selectedChannelGroups.length} Channels Selected`}
                  </Text>
                </Col>
                <Col paddingLeft='1em'>
                  <TextButton
                    allCaps
                    onClick={() =>
                      navigateToBulkUpdateRoute(
                        RouteChannelGroupBulkUpdate,
                        selectedChannelGroups as number[] | string[],
                        extractRouteParams({ session, user }),
                      )
                    }
                  >
                    <Cols style={{ alignItems: 'center' }}>
                      <Col displayFlex>
                        <EditIcon style={{ height: '0.65em', color: BULK_ACTION_COLOR }} />
                      </Col>
                      <Col>
                        <Text small bold color={BULK_ACTION_COLOR}>
                          Bulk Update
                        </Text>
                      </Col>
                    </Cols>
                  </TextButton>
                </Col>
                <Col paddingLeft='1em'>
                  <TextButton allCaps onClick={() => setDeleteModalActive(true)}>
                    <Cols style={{ alignItems: 'center' }}>
                      <Col displayFlex>
                        <DeleteIcon style={{ height: '0.65em', color: BULK_ACTION_COLOR }} />
                      </Col>
                      <Col>
                        <Text small bold color={BULK_ACTION_COLOR}>
                          Delete
                        </Text>
                      </Col>
                    </Cols>
                  </TextButton>
                </Col>
              </Cols>
            </Row>
          )}

          {/* TABLE */}
          <Row noHorizontalPadding>
            <ListViewTable
              activeColumnKeys={Object.values(activeColumns)
                .filter((column) => column.selected)
                .map((column) => column.ID)}
              columns={cfg.table.columns}
              displayNameWithID={!isDemoAccount(user.user.Username)}
              isExpanded={isTableExpanded}
              enableActions
              enableSelectCheckbox
              customFunctions={{
                handleClickOnName: (id: string) => {
                  const selected = channelGroups.find((group) => String(group.ID) === id);
                  if (selected) {
                    setActiveChannelGroup(selected);
                  }
                },
                handleClickOnChannels: (channelIDs: string[]) => {
                  setActiveChannels(
                    channels.filter((channel) => channel.ID && channelIDs.includes(channel.ID)),
                  );
                },
              }}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleCloneItem={handleCloneChannelGroup}
              handleDeleteItem={(id: string) => deleteChannelGroups([id])}
              handleOnChangeSelect={handleChangeSelectedChannels}
              handleOnClickColumnHeader={handleClickColumnHeader}
              handleSelectAllClick={handleSelectAllChannels}
              isActionPending={isActionPending}
              isReadAuthorized={readAuthorization.isAuthorized}
              isSuperUser={isSuperUser}
              isWriteAuthorized={writeAuthorization.isAuthorized}
              loadState={loadState}
              order={order}
              orderBy={orderBy}
              page={currPage}
              rows={formatDataToLVTRows(channelGroups, cfg.table)}
              rowsPerPage={rowsPerPage}
              selected={selectedChannelGroups}
              totalRowCount={totalRows}
              className={cypressClassName}
            />
          </Row>
        </Rows>
      </Panel>

      {/* EDIT OR CREATE CHANNEL GROUP MODAL */}
      {activeChannelGroup && (
        <GroupSettingsDialog
          menuTitle='Channel'
          active={!!activeChannelGroup}
          groupInfo={activeChannelGroup}
          list={channels.reduce((acc, channel) => {
            if (channel.ID) {
              acc[channel.ID] = {
                ID: channel.ID,
                title: channel.Name ?? '',
                selected: activeChannelGroup?.ChannelIDs?.includes(channel.ID),
              };
            }
            return acc;
          }, {} as IMenuList)}
          onClose={() => setActiveChannelGroup(null)}
          onUpdate={(name: string, _channelIDs: [string], defaultWeight: number) => {
            if (activeChannelGroup?.ID === '0') {
              handleCreateChannelGroups(name, defaultWeight);
            } else {
              handleUpdateChannelGroups(name, defaultWeight);
            }
          }}
          handleItemAcceptClick={(channelIDs: IMenuList) => {
            const selected = Object.values(pickBy(channelIDs, ({ selected }) => selected)).map(
              ({ ID }) => ID,
            );
            setActiveChannelGroup({ ...activeChannelGroup, ChannelIDs: selected });
            return selected;
          }}
          selectedGroupLabels={activeChannelGroup?.ChannelGroupLabels}
          loadingGroups={loadState === LoadingStateLoading}
          allGroupLabels={labels}
          handleChangeSelectedGroupLabels={(newLabels: Array<{ [key: string]: string | number }>) =>
            setActiveChannelGroup({
              ...activeChannelGroup,
              ChannelGroupLabels: newLabels.map((label) => ({
                ID: typeof label.ID === 'string' ? parseInt(label.ID, 10) : label.ID,
                Name: String(label.Name),
              })),
            })
          }
          showPrefixID={true}
        />
      )}

      {/* DELETE MODAL */}
      {deleteModalActive && (
        <ModelDialogDeleteView
          disableSnackbar
          active={deleteModalActive}
          model='Channels'
          Name={
            selectedChannelGroups.length > 1
              ? `${selectedChannelGroups.length} Channels`
              : selectedChannelGroups[0]
          }
          ID={
            selectedChannelGroups.length > 1
              ? `${selectedChannelGroups.length} Channels`
              : selectedChannelGroups[0]
          }
          deleteModel={() => {
            const res = deleteChannelGroups(selectedChannelGroups);
            return res;
          }}
          onDelete={() => {
            unstable_batchedUpdates(() => {
              setDeleteModalActive(false);
              setSelectedChannelGroups([]);
            });
          }}
          onError={() => setDeleteModalActive(false)}
          onClose={() => setDeleteModalActive(false)}
        />
      )}

      {/* CHANNELS MODAL */}
      {activeChannels && (
        <Dialog
          fullWidth
          maxWidth='sm'
          keepMounted
          open={!!activeChannels}
          onClose={() => setActiveChannels(null)}
        >
          <ModalSelectTable
            title='Channels'
            singularTitle='Channel'
            items={activeChannels}
            onSelectRow={(id: string) => {
              navigate(RouteChannelExisting, extractRouteParams({ session, user }), { ch: id });
            }}
            interactive={true}
          />
        </Dialog>
      )}
    </Panels>
  );
};
